/**
 * Pure TEA update for editor cloud sync (push, pull on open, realtime watch).
 *
 * Shell: `useEditorCloudStore.ts` (Pinia, `syncFromCloud`, `kickCloudSync`,
 * `watchProjectTips`). The shell passes environment facts in each Msg and
 * reports async outcomes back as Msgs; this module only decides.
 */
import { assertNever } from "../lib/assertNever";
import { isJust, isNothing, type Maybe } from "../lib/maybe";
import type { UpdateFn } from "../lib/tea";

/** Mirrors `SyncFromCloudResult["status"]` (update modules may not import cloud libs). */
export type CloudSyncStatus =
  | "skipped"
  | "unchanged"
  | "local_ahead"
  | "fast_forwarded"
  | "resolved_keep_local"
  | "resolved_use_cloud"
  | "diverged";

/** True when the pull replaced local files, so the editor must reload. */
export function syncUpdatedWorkingTree(status: CloudSyncStatus): boolean {
  switch (status) {
    case "fast_forwarded":
    case "resolved_use_cloud":
      return true;
    case "skipped":
    case "unchanged":
    case "local_ahead":
    case "resolved_keep_local":
    case "diverged":
      return false;
    default:
      return assertNever(status);
  }
}

export type EditorCloudModel = {
  /** Last observed cloud sign-in (refreshed when a push is attempted). */
  readonly signedIn: boolean;
  /** Realtime watching may start only after the editor says it is ready. */
  readonly realtimeReady: boolean;
  /** Bumped on every (re)start or stop so late async starts can be discarded. */
  readonly realtimeStartId: number;
  /** Key of the live watch, or `""` when none. */
  readonly realtimeKey: string;
  readonly watching: boolean;
};

export function initialEditorCloudModel(signedIn: boolean): EditorCloudModel {
  return {
    signedIn,
    realtimeReady: false,
    realtimeStartId: 0,
    realtimeKey: "",
    watching: false,
  };
}

/** Facts for the manual / blur / open sync guards. */
export type CloudSyncEnv = {
  readonly tauri: boolean;
  readonly rootPath: Maybe<string>;
  /** Fresh `getCloudSession() !== null`. */
  readonly signedIn: boolean;
  /** `cloudSyncBusy.value` */
  readonly busy: boolean;
};

/** Facts for the realtime watch decision. */
export type RealtimeEnv = {
  readonly tauri: boolean;
  readonly kind: string;
  readonly rootPath: Maybe<string>;
  readonly signedIn: boolean;
  /** Trimmed cloud project id, `""` when the project has none. */
  readonly cloudProjectId: string;
};

export function realtimeWatchKey(env: RealtimeEnv): string {
  return [
    env.kind,
    isJust(env.rootPath) ? env.rootPath.value : "",
    env.signedIn ? "1" : "0",
    env.cloudProjectId,
  ].join("\0");
}

export type PullResult =
  | { readonly kind: "synced"; readonly status: CloudSyncStatus }
  | { readonly kind: "failed" };

export type EditorCloudMsg =
  | { readonly type: "PushImmediateRequested"; readonly env: CloudSyncEnv }
  | { readonly type: "ManualSyncRequested"; readonly env: CloudSyncEnv }
  | { readonly type: "BlurSyncRequested"; readonly env: CloudSyncEnv }
  | { readonly type: "PullOnOpenRequested"; readonly env: CloudSyncEnv }
  | {
      readonly type: "PullFinished";
      readonly rootPath: string;
      readonly result: PullResult;
    }
  | { readonly type: "OpenPullReloaded"; readonly rootPath: string }
  | { readonly type: "TipSynced"; readonly status: CloudSyncStatus }
  | { readonly type: "RealtimeRequested"; readonly env: RealtimeEnv }
  | { readonly type: "RealtimeReady" }
  | {
      readonly type: "WatchStarted";
      readonly startId: number;
      readonly key: string;
    }
  | { readonly type: "Disposed" };

export type EditorCloudCmd =
  | {
      readonly kind: "kickSync";
      readonly rootPath: string;
      readonly source: "immediate" | "blur";
    }
  | { readonly kind: "flushSaveThenPush" }
  | { readonly kind: "pullFromCloud"; readonly rootPath: string }
  | { readonly kind: "reloadAfterOpenPull"; readonly rootPath: string }
  | { readonly kind: "reloadFromDisk" }
  | { readonly kind: "ensureBackup"; readonly rootPath: string }
  | {
      readonly kind: "startWatch";
      readonly rootPath: string;
      readonly key: string;
      readonly startId: number;
    }
  | { readonly kind: "stopWatch" }
  | { readonly kind: "adoptStartedWatch" }
  | { readonly kind: "discardStartedWatch" }
  | { readonly kind: "requestRealtime" };

type Result = readonly [EditorCloudModel, readonly EditorCloudCmd[]];

function stay(model: EditorCloudModel): Result {
  return [model, []];
}

export function updateEditorCloud(
  model: EditorCloudModel,
  msg: EditorCloudMsg,
): Result {
  switch (msg.type) {
    case "PushImmediateRequested": {
      const { env } = msg;
      if (env.tauri === false || isNothing(env.rootPath)) return stay(model);
      const next = { ...model, signedIn: env.signedIn };
      if (env.signedIn === false) return [next, []];
      return [
        next,
        [
          {
            kind: "kickSync",
            rootPath: env.rootPath.value,
            source: "immediate",
          },
        ],
      ];
    }
    case "ManualSyncRequested": {
      const { env } = msg;
      if (env.tauri === false || isNothing(env.rootPath)) return stay(model);
      if (env.busy || env.signedIn === false) return stay(model);
      return [model, [{ kind: "flushSaveThenPush" }]];
    }
    case "BlurSyncRequested": {
      const { env } = msg;
      if (isNothing(env.rootPath) || env.signedIn === false || env.busy) {
        return stay(model);
      }
      return [
        model,
        [{ kind: "kickSync", rootPath: env.rootPath.value, source: "blur" }],
      ];
    }
    case "PullOnOpenRequested": {
      const { env } = msg;
      if (env.tauri === false || isNothing(env.rootPath)) return stay(model);
      if (env.signedIn === false) return stay(model);
      return [model, [{ kind: "pullFromCloud", rootPath: env.rootPath.value }]];
    }
    case "PullFinished": {
      // The canonical-backup check runs after every open pull, success or not.
      if (
        msg.result.kind === "synced" &&
        syncUpdatedWorkingTree(msg.result.status)
      ) {
        return [
          model,
          [{ kind: "reloadAfterOpenPull", rootPath: msg.rootPath }],
        ];
      }
      return [model, [{ kind: "ensureBackup", rootPath: msg.rootPath }]];
    }
    case "OpenPullReloaded":
      return [model, [{ kind: "ensureBackup", rootPath: msg.rootPath }]];
    case "TipSynced":
      return syncUpdatedWorkingTree(msg.status)
        ? [model, [{ kind: "reloadFromDisk" }]]
        : stay(model);
    case "RealtimeRequested": {
      if (model.realtimeReady === false) return stay(model);
      const { env } = msg;
      const canWatch =
        env.tauri &&
        isJust(env.rootPath) &&
        env.signedIn &&
        env.cloudProjectId.length > 0;
      if (canWatch === false || isNothing(env.rootPath)) {
        return [
          {
            ...model,
            realtimeStartId: model.realtimeStartId + 1,
            realtimeKey: "",
            watching: false,
          },
          [{ kind: "stopWatch" }],
        ];
      }
      const key = realtimeWatchKey(env);
      if (model.watching && model.realtimeKey === key) return stay(model);
      const startId = model.realtimeStartId + 1;
      return [
        {
          ...model,
          realtimeStartId: startId,
          realtimeKey: "",
          watching: false,
        },
        [
          { kind: "stopWatch" },
          { kind: "startWatch", rootPath: env.rootPath.value, key, startId },
        ],
      ];
    }
    case "RealtimeReady":
      return [{ ...model, realtimeReady: true }, [{ kind: "requestRealtime" }]];
    case "WatchStarted": {
      if (msg.startId !== model.realtimeStartId) {
        return [model, [{ kind: "discardStartedWatch" }]];
      }
      return [
        { ...model, realtimeKey: msg.key, watching: true },
        [{ kind: "adoptStartedWatch" }],
      ];
    }
    case "Disposed":
      return [
        {
          ...model,
          realtimeReady: false,
          realtimeStartId: model.realtimeStartId + 1,
          realtimeKey: "",
          watching: false,
        },
        [{ kind: "stopWatch" }],
      ];
    default:
      return assertNever(msg);
  }
}

export const updateEditorCloudFn: UpdateFn<
  EditorCloudModel,
  EditorCloudMsg,
  EditorCloudCmd
> = updateEditorCloud;
